import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DfApiConfigService } from '@df/api/config/df-api-config.service';
import { IRequestCallback } from '@df/api/request/request.service';
import { HashStorageService } from '@df/browser/storage/hash-storage.service';
import { StoreFinderComponentDef } from '@df/catalog-ui/store/finder/store-finder.component.def';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { IStoreData, IStoreDataV3, Store } from '@df/catalog/store/store';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { MuiInputDirective } from '@df/legacy/ui/form/mui-input.directive';
import { DfUiModule } from '@df/ui/df-ui.module';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { Nexus } from '@tomandco/nexus-api-types';
import compact from 'lodash-es/compact';
import sortBy from 'lodash-es/sortBy';
import uniq from 'lodash-es/uniq';
import { NStoreModal } from '../../../cms/modal/store/store-modal-component';
import { GoogleMapComponent } from '../../../module/google-maps/google-map.component';
import { ActionComponent } from '../../../ui/api/action.component';
import { InputWrapBodyDirective } from '../../../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../../../ui/form/input-wrap.component';
import { ResultComponent } from '../../../ui/resut/result.component';
import { IPageStoreFinderData } from '../page/component/store-finder/store-finder.component';
import { StoreOpenStatusComponent } from '../page/component/store-open-status/store-open-status.component';
import { LocalStore } from '../../model/store/store.decorator';
declare module '@df/catalog/store/store' {
    export interface IStoreDataV1 {
        store_name: string;
        city?: string;
        days?: IDaysData;
        brands: Store[];
        address?: string;
        state?: string;
    }
}
@Component({
    selector: 'store-finder',
    templateUrl: 'store-finder.html',
    styleUrls: ['store-finder.scss'],
    standalone: true,
    imports: [
        DfUiModule,
        TranslationModule,
        NgTemplateOutlet,
        ResultComponent,
        GoogleMapComponent,
        StoreOpenStatusComponent,
        InputWrapComponent,
        InputWrapBodyDirective,
        FormsModule,
        MuiInputDirective,
        ActionComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StoreFinderComponent extends StoreFinderComponentDef<LocalStore<IStoreDataV3>, IStoreDataV3> {
    @Input()
    brandFilter?: 'Helmet' | 'all';
    @Input()
    hideTags?: boolean;
    @ViewChild('addressInput')
    addressInput?: ElementRef;
    component?: IPageStoreFinderData;
    /**
     * country options for selector
     */
    countries: Nexus.Lib.General.Country[] = [];
    defaultCountry?: Nexus.Lib.General.Country;
    isCurrentLocationVisible = false;
    override googleMapOptions = {
        mapTypeControl: false,
        zoomControl: false,
        scaleControl: false,
        streetViewControl: false,
        fullscreenControl: false
    };
    private _isMapVisible = false;
    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private app: App,
        private hashStorage: HashStorageService,
        private dfApiConfigService: DfApiConfigService,
        private dfModalService: DfModalService
    ) {
        super(changeDetectorRef);
    }
    override ngOnInit() {
        this.address = this.locationState;
        this.country = this.hashStorage.get('country');
        if (this.address) {
            this.search(this.initialStatus);
        } else {
            this.loadAndShowAll();
        }
        this.dfApiConfigService.loadCountries().then(() => {
            this.finderService.resource.loadAll(1).then(result => {
                // all country ids where stores are located
                const avaibleCountryIds = <string[]>compact(uniq(result.map(store => store.data?.country_id)));
                // sets countries list to show only those that have any stores
                this.countries = (this.app.apiConfigService.countries || [])
                    .filter(country => avaibleCountryIds.includes(country.code))
                    .sort((a, b) => (a.name < b.name ? -1 : 1));
                this.defaultCountry = this.countries.find(country => country.code === this.app.data.defaultCountry);
                if (this.defaultCountry && this.app.data.defaultCountry !== 'IE') {
                    this.country = this.defaultCountry.code;
                    this.search();
                }
                this.detectChanges();
            });
        });
    }

    override search(
        status = this.status,
        findData?: Catalog.IFindableLocateOptions | Catalog.IFindableLocateCoordsOptions,
        onError?: IRequestCallback
    ): void {
        if (this.form) {
            this.form.submitAttempt = true;
            this.form.ngSubmit.emit();
        }

        const unsubmit = () => {
            if (this.form) {
                this.form.submitAttempt = false;
            }
        };

        if (!this.form || this.form.valid) {
            if (!findData) {
                findData = {
                    address: <string>this.address,
                    country: this.country,
                    maxDistance: this.maxDistance,
                    verbosity: this.loacateVerbosity
                };
            }

            this.locationState = this.address;

            if (this.limit && findData.limit === undefined) {
                findData.limit = this.limit;
            }

            const hash = JSON.stringify(findData);

            if (this.lastSearchHash !== hash) {
                this.lastSearchHash = hash;

                if (!onError) {
                    onError = () => this.loadAndShowAll();
                }

                if (
                    this.finderService.locate(
                        status,
                        findData,
                        (response: LocalStore<IStoreDataV3>[]) => {
                            unsubmit();

                            const filteredResponse = this.applyBrandFilter(response);

                            this.findableModels = sortBy(
                                uniq(
                                    filteredResponse.filter(
                                        findableModel => findableModel.data && findableModel.data.latitude && findableModel.data.longitude
                                    )
                                ),
                                'distance'
                            );
                            this._isUserSearch = true;

                            this.onFindableModelsUpdate();
                        },
                        onError
                    ) !== false
                ) {
                    return;
                }

                this.loadAndShowAll();
                unsubmit();
            }
        }
    }

    override updateAllFindableModels(): void {
        if (!this._isUserSearch) {
            this.findableModels = sortBy(this.applyBrandFilter(this.allFindableModels), model => model.data!.sort_order);
            this.onFindableModelsUpdate();
        }
    }

    private applyBrandFilter(stores: LocalStore<IStoreDataV3>[]): LocalStore<IStoreDataV3>[] {
        const brandFilter = this.brandFilter;
        if (!brandFilter || brandFilter === 'all') {
            return stores;
        }

        return stores.filter(store => store.data?.tags?.map(el => el.tag_name.includes(brandFilter)));
    }

    get isMapVisible(): boolean {
        return this._isMapVisible;
    }
    set isMapVisible(value: boolean) {
        if (this._isMapVisible !== value) {
            this._isMapVisible = value;
            this.detectChanges();
        }
    }
    override detectChanges() {
        this.changeDetectorRef.detectChanges();
    }
    override loadAndShowAll(status?: Status): void {
        this.finderService.loadAll(status, 1, () => {
            this._isUserSearch = false;
            this.updateAllFindableModels();
        });
    }
    onCountryChange() {
        // store country in hash storage
        this.hashStorage.set('country', this.country);
        this.search();
    }
    override searchByGeolocation(status = this.status, alwaysTrigger?: boolean, useStorage?: boolean): void {
        // reset country on geolocation search
        this.country = undefined;
        this.hashStorage.set('country', undefined);
        super.searchByGeolocation(status, alwaysTrigger, useStorage);
    }
    showStoreModal(store: LocalStore<IStoreDataV3>) {
        const data: NStoreModal.IModalData = {
            store,
            hideTags: this.hideTags || false
        };
        this.dfModalService.open(undefined, NStoreModal.id, undefined, data);
    }
    keyup(event: KeyboardEvent) {
        if (event.key === 'Enter' || event.code === 'Enter') {
            this.addressInput?.nativeElement.blur();
            this.search();
        }
    }
}
